import axios from "axios";
import { AuthStore } from "../store/authStore";
import { userSession } from "../components/security/cognito";
import moment from "moment";

export const URL =
  process.env.REACT_APP_ENV === "development"
    ? "https://api-dev.code4pro.com/provicta-service"
    // ? "http://127.0.0.1:5000/provicta-service"
    : "https://api.code4pro.com/provicta-service";

const headers = () => {
  return {
    Authorization: `Bearer ${AuthStore.session.getIdToken().getJwtToken()}`,
  };
};

axios.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    let originalConfig = err.config;
    if (err.response.status === 401) {
      if (AuthStore.authenticated && AuthStore.session != null) {
        try {
          originalConfig._retry = true;
          const session = await userSession(AuthStore.session);
          AuthStore.interceptorSetSession(session);
          originalConfig.headers = headers();
          originalConfig.headers["content-type"] = "application/json";
          return axios(originalConfig);
        } catch (e) {
          return Promise.reject(e);
        }
      }
    } else {
      return Promise.reject(err);
    }
  }
);

export async function getMerticTimepoints(userid, startDate, endDate) {
  return (
    await axios.get(
      `${URL}/timeseries/${userid}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
}

export async function getMerticTimepointsLimit(userid, limit) {
  return (
    await axios.get(`${URL}/timeseries/${userid}?limit=${limit}`, {
      headers: headers(),
    })
  ).data;
}

export async function getUserStats(userCode) {
  return (
    await axios.get(`${URL}/users/${userCode}/metric-stats?percentile=0.7`, {
      headers: headers(),
    })
  ).data;
}

export async function getRawTimeseries(userid, startDate, endDate) {
  return (
    await axios.get(
      `${URL}/timeseries-data/${userid}/heart_rate?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
}

export async function getRawTimeseriesLimit(userid, limit) {
  return (
    await axios.get(
      `${URL}/timeseries-data/${userid}/heart_rate?limit=${limit}`,
      { headers: headers() }
    )
  ).data;
}

export async function getEvents(userid, startDate, endDate) {
  return (
    await axios.get(
      `${URL}/events/${userid}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
}

export async function getUserIds() {
  return (await axios.get(`${URL}/userids`, { headers: headers() })).data;
}

export async function getUser() {
  return (await axios.get(`${URL}/user`, { headers: headers() })).data.data;
}

export async function getUserById(userId) {
  return (await axios.get(`${URL}/user/${userId}`, { headers: headers() })).data
    .data;
}

export async function getUsers() {
  return (await axios.get(`${URL}/users`, { headers: headers() })).data.data;
}

export async function getOrganizations() {
  return (await axios.get(`${URL}/org`, { headers: headers() })).data.data;
}

export async function getOrganizationMappings(id) {
  return (await axios.get(`${URL}/org/${id}/userids`, { headers: headers() }))
    .data.data;
}

export async function deleteOrganizationUser(id, userId) {
  return (
    await axios.delete(
      `${URL}/org/${id}/userids/${userId}`,
      {},
      { headers: headers() }
    )
  ).data;
}

export async function cerateOrganization(data) {
  return (await axios.post(`${URL}/org`, data, { headers: headers() })).data
    .data;
}
export async function createOrganizationUserId(id, data) {
  return (
    await axios.post(`${URL}/org/${id}/userids`, data, { headers: headers() })
  ).data.data;
}

export async function calculateMetrics(
  userid,
  range_start,
  range_end,
  span = 100,
  slide = 15,
  dbwrite = true
) {
  const data = {
    detail: {
      userid: userid,
      range_start: range_start,
      range_end: range_end,
      span: span,
      slide: slide,
      dbwrite: dbwrite,
    },
  };
  return (
    await axios.post(`${URL}/analytics/compute`, data, { headers: headers() })
  ).data;
}

export async function associateUser(userIdCode) {
  return (
    await axios.post(
      `${URL}/user/associate`,
      { userIdCode: userIdCode },
      { headers: headers() }
    )
  ).data.data;
}

export async function inviteUser(
  email,
  firstname,
  lastname,
  role,
  organization
) {
  if (role === "admin") {
    organization = "admin_org";
  }
  const data = {
    firstname,
    email,
    username: email,
    lastname,
    roles: [role],
    org: organization,
  };
  return (await axios.post(`${URL}/user`, data, { headers: headers() })).data;
}

export async function updateUser(
  email,
  firstname,
  lastname,
  organization,
  role,
  resend = false
) {
  const data = {
    firstname,
    email,
    username: email,
    lastname,
    organization,
    roles: [role],
  };
  const params = {};
  if (resend) {
    params["resend"] = true;
  }
  return (
    await axios.put(`${URL}/user`, data, { headers: headers(), params: params })
  ).data;
}

export async function getAggregationStatusColor(
  userid,
  startDate,
  endDate,
  period
) {
  const data = (
    await axios.get(
      `${URL}/aggregation/${userid}/string/key/status-color/period/${period}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
  return data.sort((x, y) => new Date(x.timestamp) - new Date(y.timestamp));
}

export async function getAggregationStatusLabel(
  userid,
  startDate,
  endDate,
  period
) {
  const data = (
    await axios.get(
      `${URL}/aggregation/${userid}/string/key/status-label/period/${period}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
  return data.sort((x, y) => new Date(x.timestamp) - new Date(y.timestamp));
}

export async function getAggregationHeartRate(
  userid,
  startDate,
  endDate,
  period
) {
  const data = (
    await axios.get(
      `${URL}/aggregation/${userid}/bars/key/heart_rate/period/${period}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
  return data
    .filter((x) => x.min > 0)
    .sort((x, y) => new Date(x.timepoint) - new Date(y.timepoint));
}

export async function getAggregationRRIntervals(
  userid,
  startDate,
  endDate,
  period
) {
  const data = (
    await axios.get(
      `${URL}/aggregation/${userid}/bars/key/rr_interval/period/${period}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
  return data
    .filter((x) => x.min > 0)
    .filter((x) => x.max < 2000)
    .sort((x, y) => new Date(x.timepoint) - new Date(y.timepoint));
}

export async function getAggregationSDNN(userid, startDate, endDate, period) {
  const data = (
    await axios.get(
      `${URL}/aggregation/${userid}/bars/key/sdnn/period/${period}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers() }
    )
  ).data;
  return data.sort((x, y) => new Date(x.timepoint) - new Date(y.timepoint));
}

export async function getAggregationSDNNStats(
  userid,
  startDate,
  endDate,
  period,
  multiplier = 30
) {
  return (
    await axios.get(
      `${URL}/aggregation/${userid}/stats/key/sdnn/period/${period}?start_date=${startDate}&end_date=${endDate}`,
      { headers: headers(), params: { window_multiplier: multiplier } }
    )
  ).data;
}

export async function getRuleConfig(userid) {
  return (
    await axios.get(`${URL}/rule-config/${userid}`, { headers: headers() })
  ).data;
}

export async function updateRuleConfig(userid, payload) {
  return (
    await axios.put(`${URL}/rule-config/${userid}`, payload, {
      headers: headers(),
    })
  ).data;
}

export async function getTimeStreamStats(
  userCode,
  key,
  keys,
  startDate,
  endDate
) {
  return (
    await axios.get(`${URL}/timestream/${userCode}/stats/${key}`, {
      headers: headers(),
      params: {
        start_date: startDate,
        end_date: endDate,
        keys: keys.join(","),
      },
    })
  ).data;
}

export async function getTimeStreamData(
  userCode,
  key,
  startDate,
  endDate,
  limit = null,
  lastHour = "",
  daily = "",
) {
  return (
    await axios.get(`${URL}/timestream/${userCode}/${key}`, {
      headers: headers(),

      params: {
        start_date: startDate,
        end_date: endDate,
        limit: limit,
        last_hour: lastHour,
        daily: daily
      },
    })
  ).data;
}

export async function getTimeStreamAlertBucket(
  userCode,
  key,
  startDate,
  endDate
) {
  return (
    await axios.get(`${URL}/timestream/${userCode}/alert-bucket/${key}`, {
      headers: headers(),
      params: { start_date: startDate, end_date: endDate },
    })
  ).data;
}

export async function getTimeStreamAlertHourlyBin(
  userCode,
  startDate,
  endDate
) {
  return (
    await axios.get(`${URL}/timestream/${userCode}/alert-bin`, {
      headers: headers(),
      params: { start_date: startDate, end_date: endDate },
    })
  ).data;
}

export async function updateProfile(
  userId,
  imageUrl,
  firstName,
  lastName,
  phone,
  age,
  weight,
  heightFt,
  heightIn
) {
  const data = {
    firstname: firstName,
    lastname: lastName,
    phone,
    weight,
    age,
    height_ft: heightFt,
    height_in: heightIn,
    photo: imageUrl,
  };
  const params = {};
  return (
    await axios.post(`${URL}/profile/${userId}`, data, {
      headers: headers(),
      params: params,
    })
  ).data;
}

export async function generateFileUrl() {
  return (
    await axios.post(
      `${URL}/file`,
      {},
      {
        headers: headers(),
      }
    )
  ).data;
}

export async function getFileImage(key) {
  return (
    await axios.get(`${URL}/file`, {
      headers: headers(),
      params: { key: key },
    })
  ).data;
}

export async function getUserByUserCode(userCode) {
  return (
    await axios.get(`${URL}/user-code/${userCode}`, { headers: headers() })
  ).data.data;
}

export async function updateLabel(userCode, label) {
  const data = {
    user_code: userCode,
    label,
    created_date: moment.utc().toISOString(),
  };
  return (await axios.post(`${URL}/label`, data)).data;
}


export async function resetProvisioning(userCode) {
  return (await axios.delete(`${URL}/provision-detach/${userCode}`)).data;
}

export async function getLogsUserCodes() {
  return (
    await axios.get(`${URL}/logs`, { headers: headers() })
  ).data.data;
}


export async function getLogsbyUsercode(userCode) {
  return (
    await axios.get(`${URL}/logs/${userCode}`, { headers: headers() })
  ).data.data;
}

export async function getSummary(userCode) {
  return (
    await axios.get(`${URL}/summary/${userCode}`, { headers: headers() })
  ).data;
}

export async function getAggMetrics(userCode, startDate, endDate, minutes) {
  const params = {
    start_date: startDate,
    end_date: endDate,
    aggregation: true,
    minutes: minutes
  }
  return (
    await axios.get(`${URL}/timestream/${userCode}/hrv`, { headers: headers(), params: params })
  ).data;
}
